import { Formik } from "formik";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import { Box } from "../../components/Box";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H2, H3 } from "../../components/Heading";
import { Label } from "../../components/Label";
import { Paragraph } from "../../components/Paragraph";
import Select from "../../components/Select";
import { useAddAddress } from "../../data/useAddAddress";
import { useCountries } from "../../data/useCountries";
import { showError } from "../../utils/error-handling";
import { NewChecksFrame } from "./NewChecksFrame";
import { CheckAddress } from "../../types/CheckAddress";
import useAddressLongForm from "../../utils/forms/useAddressLongForm";
import { addressFormValidationSchema } from "../../utils/validationSchema";
import { useIndividual } from "../../data/useIndividual";
import { Input } from "../../components/Input";

const Header = () => (
  <>
    <H2>Add address</H2>
    <Paragraph>
      Please confirm the individual's current primary residential address
    </Paragraph>
  </>
);

export const AddAddress = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const mandateId = parseInt(params.id);
  const { data } = useCountries();
  const [selectedCountry, setSelectedCountry] = useState<Option | null>(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showNoCoverageBox, setShowNoCoverageBox] = useState(null);
  const [showNationalIdBox, setShowNationalIdBox] = useState(null);
  const [nationalIdRegex, setNationalIdRegex] = useState(null);
  const [nationalIdNumber, setNationalIdNumber] = useState("");
  const { mutate: addAddress, isLoading } = useAddAddress();
  const [formOpen, setFormOpen] = useState(false);
  const { AddressLongForm } = useAddressLongForm();
  const [nationalIdError, setNationalIdError] = useState("");

  const { data: individualData } = useIndividual(params.individualId);

  const hasAddressCheck = individualData?.data.all_checks.some(
    (check) => check.name === "address_verification_check"
  );
  const showAddressStep = location.state?.showAddressStep ?? true;

  // const showMonitoringStep = location.state?.showMonitoringStep ?? true;

  const isPepAndSanctionsSelected = location.state?.isPepAndSanctionsSelected;

  const isAdverseMediaSelected = location.state?.isAdverseMediaSelected;

  type Option = {
    label: string;
    value: string | number;
    sac: boolean;
  };

  const countryOptions = data?.data.map(({ isoalpha2, name, sac }) => ({
    label: name,
    value: isoalpha2,
    sac: sac,
  })) as Option[];

  const handleNext = (values: CheckAddress) => {
    values.country_code = selectedCountry.value.toString();
    values.national_id_number = nationalIdNumber;
    if (values) {
      addAddress(
        {
          id: parseInt(params.individualId),
          address: values,
        },
        {
          onSuccess: (res) => {
            // if (showMonitoringStep) {
            //   navigate(
            //     `/checks/${mandateId}/monitoring/${res.data.individual}`,
            //     {
            //       state: {
            //         showAddressStep: showAddressStep,
            //         showMonitoringStep: showMonitoringStep,
            //         isPepAndSanctionsSelected: isPepAndSanctionsSelected,
            //         isAdverseMediaSelected: isAdverseMediaSelected,
            //       },
            //     }
            //   );
            // } else {
            navigate(`/screening/${mandateId}/run-screening/${res.data.individual}`, {
              state: {
                showAddressStep: showAddressStep,
                // showMonitoringStep: showMonitoringStep,
                isPepAndSanctionsSelected: isPepAndSanctionsSelected,
                isAdverseMediaSelected: isAdverseMediaSelected,
              },
            });
            // }
          },
          onError: (err: any) => {
            showError(
              err,
              "A problem occurred while creating individual address."
            );
          },
        }
      );
    }
  };

  const handleNationalIdChange = (e:React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNationalIdNumber(value);

    if(!nationalIdRegex?.test(value)){
      setNationalIdError("Invalid National ID");
    } else {
      setNationalIdError("");
    }
  }
  const initialValues: CheckAddress = {
    building_number: "",
    flat_appartment_subbuilding: "",
    building_name: "",
    road_street: "",
    town_city: "",
    district: "",
    state_province_name: "",
    state_province_code: "",
    post_zip_code: "",
    country_code: "",
    national_id_number: "",
  };

  return (
    <NewChecksFrame
      step="address"
      mandateId={mandateId}
      showAddressStep={showAddressStep}
    // showMonitoringStep={showMonitoringStep}
    >
      <Header />
      <Box position={"relative"}>
        <Select
          value={selectedCountry}
          options={countryOptions}
          onChange={(option: Option) => {
            setSelectedCountry(option);
            const showNationalId = data?.data.find((val) => val?.isoalpha2 === option?.value)?.requires_national_id?? false;
            const nationalId = data?.data.find((val) => val?.isoalpha2 === option?.value)?.national_id_validation_pattern ?? null;
            setShowNationalIdBox(showNationalId);
            setNationalIdRegex(nationalId? new RegExp(nationalId): null);
            if (!option.sac) {
              setShowNoCoverageBox(true);
            } else {
              setShowNoCoverageBox(false);
            }
          }}
          minWidth={200}
          width={600}
          flex={1}
          label="Country"
          name="country"
          isRequired
          hasError={Boolean(!selectedCountry)}
          errorMessage={selectedCountry ? "" : "Country is required"}
        />

        {showNoCoverageBox && hasAddressCheck && (
          <Box
            width={250}
            position={"fixed"}
            top={250}
            right={30}
            backgroundColor={"gray.100"}
            p={1}
            borderRadius={12}
          >
            <H3 fontSize={1}>No address coverage</H3>
            <Paragraph fontSize={0}>
              Our address verification service does not currently cover the
              country you have selected.
            </Paragraph>
          </Box>
        )}
        {selectedCountry && showNationalIdBox && (
          <Box
          width={600}
          position={"relative"}
          >
            <Paragraph>National ID card number</Paragraph>
            <Paragraph>Please add your {selectedCountry?.label} ID number.
              This will be used to help verify your identity and address</Paragraph>
            <Input
              onChange={handleNationalIdChange}
              value = {nationalIdNumber}
              hasError={Boolean(nationalIdError)}
              errorMessage={nationalIdError}
              placeholder="Enter national ID number"
              isRequired

            />
          </Box>
        )}

      </Box>

      {/* !showNoCoverageBox && */}
      {selectedCountry && (
        <Box width={600}>
          <Formik
            initialValues={initialValues}
            validationSchema={() =>
              addressFormValidationSchema(selectedCountry?.value.toString())
            }
            onSubmit={handleNext}
          >
            {(formik) => (
              <>
                <form onSubmit={formik.handleSubmit}>
                  <Box mt={2}>
                    <Flex
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Label>Address</Label>
                      <Button
                        variant="text"
                        type="button"
                        onClick={() => setFormOpen(true)}
                      >
                        Enter address manually
                      </Button>
                    </Flex>
                    <AddressAutocomplete
                      setSelectedAddress={(address) => {
                        setSelectedAddress(address);
                        setFormOpen(true);
                        Object.keys(address).forEach((key) => {
                          formik.setFieldValue(key, address[key]);
                        });
                      }}
                      country={selectedCountry.value}
                      setFormOpen={setFormOpen}
                    />
                  </Box>

                  {formOpen && <AddressLongForm formik={formik} />}

                  <Flex justifyContent="flex-end" my={3}>
                    <Button
                      type="submit"
                      isDisabled={
                        isLoading ||
                        (!selectedAddress && !(formik.isValid && formik.dirty))
                      }
                    >
                      Next
                    </Button>
                  </Flex>
                </form>
              </>
            )}
          </Formik>
        </Box>
      )}
    </NewChecksFrame>
  );
};
